import { create } from "zustand";
import { safeCallEitaaWebApp } from "@/utils/utilities";

type BackHandler = () => void;
type Entry = { id: number; handler: BackHandler };

interface BackButtonState {
  stack: Entry[];
  push: (handler: BackHandler) => number;
  remove: (id: number) => void;
  pop: () => void;
  clear: () => void;
}

let nextId = 1;
let sdkListenerAttached = false;

function getBackButton() {
  if (typeof window === "undefined") return null;
  return window.Eitaa?.WebApp?.BackButton ?? null;
}

function attachSdkListenerOnce() {
  if (sdkListenerAttached) return;
  const BackButton = getBackButton();
  if (!BackButton) return;

  const onBackClick = () => {
    const stack = useBackButtonStore.getState().stack;
    const top = stack[stack.length - 1];
    top?.handler();
  };

  const ok = safeCallEitaaWebApp(() => BackButton.onClick(onBackClick));
  if (ok) sdkListenerAttached = true;
}

function showBackButton() {
  const BackButton = getBackButton();
  if (!BackButton) return;
  safeCallEitaaWebApp(() => BackButton.show());
}

function hideBackButton() {
  const BackButton = getBackButton();
  if (!BackButton) return;
  safeCallEitaaWebApp(() => BackButton.hide());
}

export const useBackButtonStore = create<BackButtonState>((set, get) => ({
  stack: [],

  push: (handler) => {
    attachSdkListenerOnce();
    const id = nextId++;
    set((state) => ({ stack: [...state.stack, { id, handler }] }));
    showBackButton();
    return id;
  },

  remove: (id) => {
    set((state) => ({ stack: state.stack.filter((e) => e.id !== id) }));
    if (get().stack.length === 0) hideBackButton();
  },

  pop: () => {
    set((state) => ({ stack: state.stack.slice(0, -1) }));
    if (get().stack.length === 0) hideBackButton();
  },

  clear: () => {
    set({ stack: [] });
    hideBackButton();
  },
}));
